import { defineMutator } from "@rocicorp/zero";
import { z } from "zod";
import { INVITE_PREFIXES } from "../api/invites";
import type { MutateSurface } from "../message-builders";
import type { MutatorCtx } from "./messages-cores";

/**
 * Build a token schema that requires the token to carry the expected
 * type-prefix. Rejects `{ type: "workspace", token: "chinv_..." }` with a
 * clear error instead of letting the wrong-type request reach the SQL
 * helper and surfacing a confusing "invite not found" later.
 */
function tokenForType(type: keyof typeof INVITE_PREFIXES) {
  return z
    .string()
    .min(1)
    .refine((t) => t.startsWith(INVITE_PREFIXES[type]), {
      message: `token prefix does not match invite type "${type}"`,
    });
}

// ── B-2: `actions.acceptInvite` shared shell ─────────────────────────────
//
// Invite acceptance has two transports differentiated by recipient state:
//
//   • Pre-auth + no-account guest paths → REST. Preview (`GET /api/invites/
//     /:token`) and guest accept (`POST /api/guest/session` for DM/group_dm)
//     stay forever — clients without a Zero session can't call mutators.
//   • Authed paths → this mutator. Workspace/channel/client_space invites
//     accepted after signup or signin (any session state) go through here.
//
// The core is intentionally a no-op on the client. All heavy lifting
// (FOR UPDATE locking, max_uses enforcement, channel-membership cascade,
// auto-join public channels, D1 mirror dual-write) is in the
// server-mutator extension override (`server/server-mutators.ts`), which
// wraps the legacy `acceptXxxInvite` SQL helpers in
// `server/routes/invite-accept-*.ts` so we don't duplicate the logic.
//
// Mutator promises can't return server-computed values to the client
// (Zero replicates state, doesn't carry RPC responses). The UI contract
// `InviteAcceptSuccess` ({ workspace_id, channel_id, space_id,
// already_member }) is preserved by the client-side helper
// `acceptInviteViaMutator` in `apps/desktop/src/lib/invite-actions.ts`,
// which pre-derives those fields from Zero queries (invite row +
// membership table) before pushing the mutator. For DM accepts, the
// helper client-mints the new channel id (via the optional `dmChannelId`
// field below) so navigation is deterministic post-push.
//
// Discriminated union by `type` so callers must pick a branch up front
// and the server override can dispatch directly.

export const acceptInviteSchema = z.discriminatedUnion("type", [
  z.object({
    type: z.literal("workspace"),
    token: tokenForType("workspace"),
  }),
  z.object({
    type: z.literal("channel"),
    token: tokenForType("channel"),
  }),
  z.object({
    type: z.literal("client_space"),
    token: tokenForType("client_space"),
    // Workspace-scope variant of client_space requires the target workspace
    // the user is linking from. Omitted = individual scope.
    target_workspace_id: z.string().optional(),
  }),
  z.object({
    type: z.literal("dm"),
    token: tokenForType("dm"),
    /**
     * Optional client-minted UUID for the new DM channel. Pre-derived by
     * the `acceptInviteViaMutator` helper so the UI can navigate to the
     * channel immediately after the mutator resolves, without waiting
     * for Zero replication to surface the server-minted id.
     *
     * Ignored when a DM already exists between the two users — the helper
     * checks Zero for an existing channel first and only mints a fresh
     * id when needed.
     */
    dmChannelId: z.string().uuid().optional(),
  }),
  z.object({
    type: z.literal("group_dm"),
    token: tokenForType("group_dm"),
  }),
]);

export type AcceptInviteArgs = z.infer<typeof acceptInviteSchema>;

/**
 * Shared accept-invite core. Client-side: no-op (the membership rows
 * appear via Zero replication once the server commits). Server-side:
 * also a no-op at this layer — the server-mutator override
 * (`server/server-mutators.ts`) is the sole authoritative implementer
 * and runs the existing `acceptXxxInvite` SQL helpers inside the same
 * Zero/PG transaction, then schedules a post-commit D1 mirror.
 *
 * Returning `null` from the shared core is the unambiguous signal "no
 * domain write happened here." The server override does its own work
 * after this returns.
 */
export async function acceptInviteCore(_params: {
  tx: MutateSurface;
  args: AcceptInviteArgs;
  ctx: MutatorCtx;
}): Promise<null> {
  return null;
}

export const inviteActionsMutators = {
  acceptInvite: defineMutator(acceptInviteSchema, async (params) => {
    await acceptInviteCore(params);
  }),
};
